import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { supabase } from "../../lib/supabase";
import toast from "react-hot-toast";
import GlowPanel from "../ui/GlowPanel";
import { logger } from "../../utils/logger";

// WaitlistEntry matches the actual waitlist table schema
interface WaitlistEntry {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  status: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface NewEntry {
  name: string;
  email: string;
  phone: string;
  notes: string;
}

/**
 * Waitlist Manager Component
 * Manage customers on the waitlist
 */
function WaitlistManager(): JSX.Element {
  // Detect current theme from document element
  const [isLightTheme, setIsLightTheme] = useState(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("theme-light");
  });

  // Watch for theme changes
  useEffect(() => {
    if (typeof document === "undefined") return;

    const checkTheme = () => {
      setIsLightTheme(
        document.documentElement.classList.contains("theme-light"),
      );
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState<NewEntry>({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });

  useEffect(() => {
    fetchWaitlist();

    // Set up real-time subscription
    const channel = supabase
      .channel("waitlist-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "waitlist",
        },
        () => {
          fetchWaitlist();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchWaitlist = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("waitlist")
        .select("*")
        .in("status", ["pending", "waiting", "notified"])
        .order("created_at", { ascending: true });

      if (error) throw error;
      setWaitlist((data || []) as WaitlistEntry[]);
    } catch (err) {
      logger.error("Error fetching waitlist:", err);
      // Silently fail if table doesn't exist yet
    } finally {
      setLoading(false);
    }
  };

  const addToWaitlist = async () => {
    try {
      if (!newEntry.email) {
        toast.error("Email is required");
        return;
      }

      const insertData = {
        email: newEntry.email,
        name: newEntry.name || null,
        phone: newEntry.phone || null,
        notes: newEntry.notes || null,
        status: "pending",
      };

      const { error } = await supabase.from("waitlist").insert([insertData]);

      if (error) throw error;

      toast.success("Added to waitlist");
      setShowAddModal(false);
      setNewEntry({
        name: "",
        email: "",
        phone: "",
        notes: "",
      });
      fetchWaitlist();
    } catch (err) {
      logger.error("Error adding to waitlist:", err);
      toast.error("Failed to add to waitlist");
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from("waitlist")
        .update({ status })
        .eq("id", id);

      if (error) throw error;

      toast.success(`Status updated to ${status}`);
      fetchWaitlist();
    } catch (err) {
      logger.error("Error updating status:", err);
      toast.error("Failed to update status");
    }
  };

  const removeFromWaitlist = async (id: string) => {
    try {
      const { error } = await supabase.from("waitlist").delete().eq("id", id);

      if (error) throw error;

      toast.success("Removed from waitlist");
      fetchWaitlist();
    } catch (err) {
      logger.error("Error removing from waitlist:", err);
      toast.error("Failed to remove from waitlist");
    }
  };

  const getWaitTime = (createdAt: string | null): string => {
    if (!createdAt) return "Unknown";
    const minutes = Math.floor(
      (new Date().getTime() - new Date(createdAt).getTime()) / 60000,
    );
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min${minutes > 1 ? "s" : ""}`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  return (
    <GlowPanel
      glow="soft"
      radius="rounded-2xl"
      padding="p-6"
      background="bg-[rgba(255,255,255,0.02)]"
      className="h-full"
    >
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[var(--text-main)]">
            Waitlist
          </h2>
          <p className="text-sm text-muted">
            {waitlist.length} {waitlist.length === 1 ? "person" : "people"}{" "}
            waiting
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="rounded-lg border border-[var(--accent)]/30 bg-[var(--accent)]/10 px-4 py-2 text-sm font-medium text-[var(--accent)] transition hover:bg-[var(--accent)]/20"
        >
          Add to Waitlist
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-[var(--accent)]"></div>
        </div>
      ) : waitlist.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-muted">No one on the waitlist</p>
        </div>
      ) : (
        <div className="space-y-3">
          {waitlist.map((entry, index) => (
            <GlowPanel
              key={entry.id}
              glow="soft"
              radius="rounded-xl"
              padding="p-4"
              background="bg-[rgba(255,255,255,0.03)]"
              className="flex items-center gap-4 transition-all hover:border-theme-medium"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--accent)]/20">
                <span className="font-bold text-[var(--accent)]">
                  #{index + 1}
                </span>
              </div>

              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[var(--text-main)]">
                    {entry.name || "Guest"}
                  </p>
                  {entry.status === "notified" && (
                    <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-xs font-medium text-blue-300">
                      Notified
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted">
                  {entry.email}
                  {entry.phone && ` • ${entry.phone}`}
                </p>
                {entry.notes && (
                  <p className="text-xs text-muted mt-1">{entry.notes}</p>
                )}
                <p className="text-xs text-muted">
                  Waiting: {getWaitTime(entry.created_at)}
                </p>
              </div>

              <div className="flex gap-2">
                {entry.status === "pending" && (
                  <button
                    onClick={() => updateStatus(entry.id, "notified")}
                    className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-1.5 text-xs font-medium text-blue-300 transition hover:bg-blue-500/20"
                  >
                    Notify
                  </button>
                )}
                <button
                  onClick={() => removeFromWaitlist(entry.id)}
                  className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 transition hover:bg-red-500/20"
                >
                  Remove
                </button>
              </div>
            </GlowPanel>
          ))}
        </div>
      )}

      {/* Add to Waitlist Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
          style={{
            backgroundColor: isLightTheme
              ? "rgba(0, 0, 0, 0.35)"
              : "rgba(0, 0, 0, 0.5)",
          }}
          onClick={() => setShowAddModal(false)}
        >
          <GlowPanel
            glow="medium"
            radius="rounded-2xl"
            padding="p-6"
            background={
              isLightTheme
                ? "bg-[rgba(255,255,255,0.98)]"
                : "bg-[rgba(10,10,14,0.98)]"
            }
            className="w-full max-w-md"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-lg font-semibold text-[var(--text-main)]">
              Add to Waitlist
            </h3>

            <form
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                addToWaitlist();
              }}
              className="space-y-4"
            >
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--text-main)]">
                  Name
                </label>
                <input
                  type="text"
                  value={newEntry.name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewEntry({ ...newEntry, name: e.target.value })
                  }
                  className="w-full rounded-lg border border-theme bg-theme-elevated px-3 py-2 text-sm text-[var(--text-main)] placeholder:text-muted focus:border-[var(--accent)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
                  placeholder="Guest name"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--text-main)]">
                  Email *
                </label>
                <input
                  type="email"
                  value={newEntry.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewEntry({ ...newEntry, email: e.target.value })
                  }
                  className="w-full rounded-lg border border-theme bg-theme-elevated px-3 py-2 text-sm text-[var(--text-main)] placeholder:text-muted focus:border-[var(--accent)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
                  placeholder="guest@example.com"
                  required
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--text-main)]">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newEntry.phone}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNewEntry({ ...newEntry, phone: e.target.value })
                  }
                  className="w-full rounded-lg border border-theme bg-theme-elevated px-3 py-2 text-sm text-[var(--text-main)] placeholder:text-muted focus:border-[var(--accent)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
                  placeholder="Phone number"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--text-main)]">
                  Notes
                </label>
                <textarea
                  value={newEntry.notes}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                    setNewEntry({ ...newEntry, notes: e.target.value })
                  }
                  className="w-full rounded-lg border border-theme bg-theme-elevated px-3 py-2 text-sm text-[var(--text-main)] placeholder:text-muted focus:border-[var(--accent)]/50 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30"
                  placeholder="Any special notes..."
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 rounded-lg border border-theme px-4 py-2 text-sm font-medium text-muted transition hover:border-theme-medium hover:text-[var(--text-main)]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 rounded-lg border border-[var(--accent)]/30 bg-[var(--accent)]/10 px-4 py-2 text-sm font-medium text-[var(--accent)] transition hover:bg-[var(--accent)]/20"
                >
                  Add to Waitlist
                </button>
              </div>
            </form>
          </GlowPanel>
        </div>
      )}
    </GlowPanel>
  );
}

export default WaitlistManager;
